"use client";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    footer?: React.ReactNode;
}

import LogoAnimatedGreeks from "@/components/logos/LogoAnimatedGreeks";

export default function Modal({ isOpen, onClose, title, children, footer }: ModalProps) {
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        if (isOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "unset";
        }
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [isOpen]);

    if (!mounted || !isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
            <div className="bg-brand-navy border border-brand-blue/50 rounded-xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in duration-200 ring-1 ring-white/10">
                {/* Header */}
                <div className="px-6 py-4 border-b border-brand-blue/20 bg-white/5 flex items-center gap-3">
                    <LogoAnimatedGreeks className="w-8 h-8" />
                    {title && (
                        <h3 className="text-xl font-montserrat font-bold text-white tracking-wide">{title}</h3>
                    )}
                </div>

                {/* Content */}
                <div className="px-6 py-6 text-brand-gray/90 font-sans">
                    {children}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-black/20 flex justify-end gap-3 border-t border-brand-blue/10">
                    {footer ? footer : (
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-brand-orange hover:bg-brand-orange/90 text-white rounded-lg font-semibold transition-all shadow-lg hover:shadow-brand-orange/20"
                        >
                            OK
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}
